import Observable from './Observable';
import { IMedia } from './Media';

class Audio extends Observable implements IMedia {
  static EVENT = {
    LOAD_START: 'loadstart',
    LOAD: 'load',
    LOAD_ERROR: 'error',
    PLAY: 'play',
    PAUSE: 'pause',
    STOP: 'stop',
    END: 'ended',
    MUTE: 'mute',
    UNMUTE: 'unmute',
    RATE: 'ratechange',
    SEEK: 'seeked',
    VOLUME: 'volumechange',
  };

  private readonly id: string;

  private readonly url: string;

  private readonly audio: HTMLAudioElement;

  private readonly handlers: Record<string, (event: any) => void> = {};

  private readonly loadForceIn: any;

  private loadForceTimeout: any;

  constructor(id: string, url: string, loadForceIn = 0) {
    super();

    this.id = id;
    this.url = url;
    this.loadForceIn = loadForceIn;

    this.audio = document.createElement('audio');
    this.audio.id = id;
    document.body.appendChild(this.audio);

    Object.values(Audio.EVENT).forEach((event) => {
      this.handlers[event] = this.handleEvent.bind(this, event);
      this.audio.addEventListener(event, this.handlers[event]);
    });
  }

  public load = (): this => {
    const source = document.createElement('source');
    source.setAttribute('src', this.url);
    source.setAttribute('type', 'audio/mpeg');
    this.audio.appendChild(source);
    this.audio.load();
    this.handleEvent(Audio.EVENT.LOAD_START, undefined);
    return this;
  };

  public play = (): this => {
    if (this.isPlaying()) {
      return this;
    }
    this.audio.play();
    return this;
  };

  public pause = (): this => {
    this.audio.pause();
    return this;
  };

  public stop = (): this => {
    this.audio.pause();
    this.audio.currentTime = 0;
    return this;
  };

  public mute = (): this => {
    this.audio.muted = true;
    this.audio.setAttribute('muted', 'muted');
    return this;
  };

  public unMute = (): this => {
    this.audio.muted = false;
    this.audio.removeAttribute('muted');
    return this;
  };

  public volume = (value: number): this => {
    this.audio.volume = value;
    return this;
  };

  public loop = (value: boolean): this => {
    this.audio.loop = value;
    return this;
  };

  public getVolume = (): number => {
    return this.audio.volume;
  };

  public isMute = (): boolean => {
    return this.audio.muted;
  };

  public isPlaying = (): boolean => {
    return !this.audio.paused && !this.audio.ended && this.audio.currentTime > 0;
  };

  public isLooping = (): boolean => {
    return this.audio.loop;
  };

  public destroy = (): void => {
    Object.values(Audio.EVENT).forEach((eventName) => {
      this.audio.removeEventListener(eventName, this.handlers[eventName]);
    });
    this.removeAllListeners();
    document.body.removeChild(this.audio);
  };

  private handleEvent = (eventName: string, event: any, errorMessage = null) => {
    if (this.loadForceIn > 0 && eventName === Audio.EVENT.LOAD_START) {
      this.loadForceTimeout = setTimeout(this.tickLoadingForce, this.loadForceIn);
    }
    if (this.loadForceIn > 0 && eventName === Audio.EVENT.LOAD && this.loadForceTimeout) {
      clearTimeout(this.loadForceTimeout);
      this.loadForceTimeout = null;
    }
    const payload = {
      ...event,
      ...(event?.type === Audio.EVENT.LOAD_ERROR && { error: { message: event.target.error.message } }),
    };
    this.dispatchEvent(eventName, payload);
  };

  private tickLoadingForce = () => {
    this.dispatchEvent(Audio.EVENT.LOAD, {});
  };
}

export default Audio;
